/**
 * Configuration Service
 *
 * Handles all configuration-related API calls with automatic caching.
 * Cache expiry: 5 minutes
 *
 * Enterprise Features:
 * - Automatic caching with 5-minute expiry
 * - Type-safe responses
 * - Comprehensive error handling
 * - Detailed logging
 * - Backward compatibility
 */

import { apiClient } from './client';
import { configCache } from '../services/configurationCache';
import { createLogger } from '../config/environment';

const logger = createLogger('ConfigurationService');

/**
 * Country Configuration
 */
export interface Country {
  id: string;
  countryName: string;
  countryCode: string;
  mobileCountryCode: string;
  currency: string;
  currencySymbol: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Shop Type Configuration
 */
export interface ShopType {
  id: string;
  code: string;
  name: string;
  description: string;
  icon?: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Backward compatibility alias
export type ShopTypeConfig = ShopType;

/**
 * Unit of Measurement Configuration
 */
export interface UnitOfMeasurement {
  id?: string;
  code: string;
  name: string;
  displayName: string;
  category: string;
  description: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Backward compatibility alias
export type UOM = UnitOfMeasurement;

/**
 * Category Configuration
 */
export interface Category {
  id: string;
  shopTypeCode: string;
  categoryName: string;
  displayOrder: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Generic API Response
 */
interface ConfigurationResponse<T> {
  status: string;
  message?: string;
  data: T[];
}

/**
 * Cache Keys
 */
const CACHE_KEYS = {
  COUNTRIES: 'config:countries',
  SHOP_TYPES: 'config:shop-types',
  UOMS: 'config:uoms',
  CATEGORIES: 'config:categories',
} as const;

class ConfigurationService {
  /**
   * Get all active countries
   * Cached for 5 minutes
   *
   * @param options - Options object with activeOnly flag OR boolean for backward compatibility
   */
  async getCountries(options?: { activeOnly?: boolean } | boolean): Promise<Country[]> {
    // Handle backward compatibility - accept boolean or object
    const activeOnly = typeof options === 'boolean' ? options : options?.activeOnly ?? true;
    const cacheKey = `${CACHE_KEYS.COUNTRIES}:${activeOnly ? 'active' : 'all'}`;

    // Check cache first
    const cached = configCache.get<Country[]>(cacheKey);
    if (cached) {
      logger.info(`✅ Countries from cache: ${cached.length} items`);
      return cached;
    }

    try {
      logger.info('📡 Fetching countries from API', { activeOnly });

      const params = activeOnly ? { active: true } : {};
      const response = await apiClient.get<ConfigurationResponse<Country>>(
        '/api/configurations/countries',
        { params }
      );

      const countries = response.data.data || [];

      // Store in cache
      configCache.set(cacheKey, countries);

      logger.info(`✅ Countries fetched: ${countries.length} items`);
      return countries;
    } catch (err) {
      logger.error('❌ Failed to fetch countries', err);
      throw new Error('Failed to load countries');
    }
  }

  /**
   * Get all active shop types
   * Cached for 5 minutes
   *
   * @param options - Options object with activeOnly flag OR boolean for backward compatibility
   */
  async getShopTypes(options?: { activeOnly?: boolean } | boolean): Promise<ShopType[]> {
    // Handle backward compatibility - accept boolean or object
    const activeOnly = typeof options === 'boolean' ? options : options?.activeOnly ?? true;
    const cacheKey = `${CACHE_KEYS.SHOP_TYPES}:${activeOnly ? 'active' : 'all'}`;

    // Check cache first
    const cached = configCache.get<ShopType[]>(cacheKey);
    if (cached) {
      logger.info(`✅ Shop types from cache: ${cached.length} items`);
      return cached;
    }

    try {
      logger.info('📡 Fetching shop types from API', { activeOnly });

      const params = activeOnly ? { active: true } : {};
      const response = await apiClient.get<ConfigurationResponse<ShopType>>(
        '/api/configurations/shop-types',
        { params }
      );

      const shopTypes = response.data.data || [];

      // Store in cache
      configCache.set(cacheKey, shopTypes);

      logger.info(`✅ Shop types fetched: ${shopTypes.length} items`);
      return shopTypes;
    } catch (err) {
      logger.error('❌ Failed to fetch shop types', err);
      throw new Error('Failed to load shop types');
    }
  }

  /**
   * Get all active units of measurement
   * Cached for 5 minutes
   *
   * @param options - Options object with activeOnly flag OR boolean for backward compatibility
   */
  async getUnitOfMeasurements(options?: { activeOnly?: boolean } | boolean): Promise<UnitOfMeasurement[]> {
    // Handle backward compatibility - accept boolean or object
    const activeOnly = typeof options === 'boolean' ? options : options?.activeOnly ?? true;
    const cacheKey = `${CACHE_KEYS.UOMS}:${activeOnly ? 'active' : 'all'}`;

    // Check cache first
    const cached = configCache.get<UnitOfMeasurement[]>(cacheKey);
    if (cached) {
      logger.info(`✅ UOMs from cache: ${cached.length} items`);
      return cached;
    }

    try {
      logger.info('📡 Fetching UOMs from API', { activeOnly });

      const params = activeOnly ? { active: true } : {};
      const response = await apiClient.get<ConfigurationResponse<UnitOfMeasurement>>(
        '/api/configurations/uoms',
        { params }
      );

      const uoms = response.data.data || [];

      // Store in cache
      configCache.set(cacheKey, uoms);

      logger.info(`✅ UOMs fetched: ${uoms.length} items`);
      return uoms;
    } catch (err) {
      logger.error('❌ Failed to fetch UOMs', err);
      throw new Error('Failed to load units of measurement');
    }
  }

  /**
   * Backward compatibility method for getUnitOfMeasurements
   */
  async getUOMs(options?: { activeOnly?: boolean } | boolean): Promise<UOM[]> {
    return this.getUnitOfMeasurements(options);
  }

  /**
   * Get all active categories
   * Cached for 5 minutes
   *
   * @param options - Options object with activeOnly and shopTypeCode OR boolean for backward compatibility
   */
  async getCategories(options?: {
    activeOnly?: boolean;
    shopTypeCode?: string;
  } | boolean): Promise<Category[]> {
    // Handle backward compatibility - accept boolean or object
    const activeOnly = typeof options === 'boolean' ? options : options?.activeOnly ?? true;
    const shopTypeCode = typeof options === 'object' ? options.shopTypeCode : undefined;
    const cacheKey = `${CACHE_KEYS.CATEGORIES}:${activeOnly ? 'active' : 'all'}:${shopTypeCode || 'all'}`;

    // Check cache first
    const cached = configCache.get<Category[]>(cacheKey);
    if (cached) {
      logger.info(`✅ Categories from cache: ${cached.length} items`);
      return cached;
    }

    try {
      logger.info('📡 Fetching categories from API', { activeOnly, shopTypeCode });

      const params: any = {};
      if (activeOnly) params.active = true;
      if (shopTypeCode) params.shopTypeCode = shopTypeCode;

      const response = await apiClient.get<ConfigurationResponse<Category>>(
        '/api/configurations/categories',
        { params }
      );

      let categories = response.data.data || [];

      // Client-side filtering if needed (in case backend doesn't filter)
      if (shopTypeCode) {
        categories = categories.filter(cat => cat.shopTypeCode === shopTypeCode);
      }

      // Store in cache
      configCache.set(cacheKey, categories);

      logger.info(`✅ Categories fetched: ${categories.length} items`);
      return categories;
    } catch (err) {
      logger.error('❌ Failed to fetch categories', err);
      throw new Error('Failed to load categories');
    }
  }

  /**
   * Backward compatibility method - get categories by shop type
   */
  async getCategoriesByShopType(shopTypeCode: string, activeOnly: boolean = true): Promise<Category[]> {
    return this.getCategories({ activeOnly, shopTypeCode });
  }

  /**
   * Clear all configuration cache
   */
  clearCache(): void {
    configCache.clear();
    logger.info('🧹 All configuration cache cleared');
  }

  /**
   * Clear specific configuration cache
   */
  clearCountriesCache(): void {
    for (const key of [...configCache['cache'].keys()]) {
      if (key.startsWith(CACHE_KEYS.COUNTRIES)) {
        configCache.delete(key);
      }
    }
  }

  clearShopTypesCache(): void {
    for (const key of [...configCache['cache'].keys()]) {
      if (key.startsWith(CACHE_KEYS.SHOP_TYPES)) {
        configCache.delete(key);
      }
    }
  }

  clearUOMsCache(): void {
    for (const key of [...configCache['cache'].keys()]) {
      if (key.startsWith(CACHE_KEYS.UOMS)) {
        configCache.delete(key);
      }
    }
  }

  clearCategoriesCache(): void {
    for (const key of [...configCache['cache'].keys()]) {
      if (key.startsWith(CACHE_KEYS.CATEGORIES)) {
        configCache.delete(key);
      }
    }
  }
}

// Singleton instance
export const configurationService = new ConfigurationService();

// Export class for testing
export { ConfigurationService };

// Additional exports for backward compatibility
export type CountriesResponse = ConfigurationResponse<Country>;
export type ShopTypesResponse = ConfigurationResponse<ShopType>;

